/**
 * Pure TEA update for editor save / autosave.
 *
 * Shell: `useEditorSaveStore.ts` (Pinia, session writes, `saveActive`/`saveAs`,
 * autosave timer, cloud push). The shell passes facts read from the session
 * in each Msg; this module decides what to do and returns Cmds.
 */
import { assertNever } from "../lib/assertNever";
import { isNothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";

export type EditorSaveModel = {
  /** A `flushSave` write loop is in flight. */
  readonly flushing: boolean;
  /** A Save As dialog / write is in flight. */
  readonly savingAs: boolean;
};

export const initialEditorSaveModel: EditorSaveModel = {
  flushing: false,
  savingAs: false,
};

/** Public `saving` flag: any save work in flight. */
export function isSaving(model: EditorSaveModel): boolean {
  return model.flushing || model.savingAs;
}

/** Session facts a flush decision needs. */
export type SaveFacts = {
  readonly hasActivePath: boolean;
  readonly dirty: boolean;
  /** `canPersistProjectRoot(session.rootPath)` */
  readonly canPersist: boolean;
};

export type FlushDecision =
  /** Another flush is running: callers share its promise. */
  | "join"
  /** Nothing to write: resolve `true`. */
  | "noop"
  /** Cannot persist here: resolve `false`. */
  | "refuse"
  /** Begin a new write loop. */
  | "start";

export function flushDecision(
  model: EditorSaveModel,
  facts: SaveFacts,
): FlushDecision {
  if (model.flushing) return "join";
  if (facts.hasActivePath === false || facts.dirty === false) return "noop";
  if (facts.canPersist === false) return "refuse";
  return "start";
}

/** Whether the drain loop should write another snapshot. */
export function shouldDrain(dirty: boolean, hasActivePath: boolean): boolean {
  return dirty && hasActivePath;
}

export type WriteSnapshot = {
  readonly path: string;
  readonly content: string;
  readonly synopsis: string;
};

export type SessionNow = {
  readonly activePath: Maybe<string>;
  readonly doc: string;
  readonly synopsis: string;
};

export type WriteOutcome =
  /** The active file changed while writing: stop, leave dirty alone. */
  | { readonly kind: "abandoned" }
  /** Write landed. `stillDirty` when the draft moved on meanwhile. */
  | { readonly kind: "settled"; readonly stillDirty: boolean };

export function writeOutcome(
  snapshot: WriteSnapshot,
  now: SessionNow,
): WriteOutcome {
  if (isNothing(now.activePath) || now.activePath.value !== snapshot.path) {
    return { kind: "abandoned" };
  }
  return {
    kind: "settled",
    stillDirty:
      now.doc !== snapshot.content || now.synopsis !== snapshot.synopsis,
  };
}

export type EditorSaveMsg =
  | { readonly type: "FlushRequested"; readonly facts: SaveFacts }
  | { readonly type: "FlushFinished" }
  | { readonly type: "SaveAsRequested"; readonly canPersist: boolean }
  | { readonly type: "SaveAsFinished" }
  | {
      readonly type: "DocChanged";
      /** New `doc` text. */
      readonly value: string;
      /** `value` after comment / align GC (equal to `value` for map docs). */
      readonly cleaned: string;
      readonly activeMap: boolean;
      readonly suppressDirty: boolean;
      readonly hasActiveDatabase: boolean;
      /** `canPersistProjectRoot(root) && hasActivePath` */
      readonly canSchedule: boolean;
    }
  | {
      readonly type: "RestoreRequested";
      readonly content: string;
      readonly facts: SaveFacts;
    }
  | { readonly type: "SaveRequested"; readonly facts: SaveFacts }
  | { readonly type: "SaveFlushed"; readonly ok: boolean }
  | { readonly type: "BlurRequested" }
  | { readonly type: "BlurFlushed" }
  | { readonly type: "CancelAutosave" }
  | { readonly type: "DraftEdited"; readonly canSchedule: boolean };

export type EditorSaveCmd =
  | { readonly kind: "cancelAutosave" }
  | { readonly kind: "scheduleAutosave" }
  | { readonly kind: "runFlush" }
  | { readonly kind: "runSaveAs" }
  /** Re-enter the loop with `SaveAsRequested` (applies the busy gate). */
  | { readonly kind: "requestSaveAs" }
  | { readonly kind: "setSuppressDirty"; readonly value: boolean }
  | { readonly kind: "setDoc"; readonly value: string }
  | { readonly kind: "setDirty"; readonly value: boolean }
  | { readonly kind: "refreshLexicon" }
  | { readonly kind: "restoreVersion"; readonly content: string }
  | { readonly kind: "markDirtyAndFlush" }
  | { readonly kind: "pushCloud" }
  | { readonly kind: "flushForBlur" }
  | { readonly kind: "blurSyncCloud" };

type Result = readonly [EditorSaveModel, readonly EditorSaveCmd[]];

function stay(model: EditorSaveModel): Result {
  return [model, []];
}

export function updateEditorSave(
  model: EditorSaveModel,
  msg: EditorSaveMsg,
): Result {
  switch (msg.type) {
    case "FlushRequested": {
      if (flushDecision(model, msg.facts) !== "start") return stay(model);
      return [
        { ...model, flushing: true },
        [{ kind: "cancelAutosave" }, { kind: "runFlush" }],
      ];
    }
    case "FlushFinished":
      return [{ ...model, flushing: false }, []];
    case "SaveAsRequested": {
      if (msg.canPersist === false || isSaving(model)) return stay(model);
      return [{ ...model, savingAs: true }, [{ kind: "runSaveAs" }]];
    }
    case "SaveAsFinished":
      return [{ ...model, savingAs: false }, []];
    case "DocChanged": {
      if (msg.activeMap) {
        if (msg.suppressDirty) {
          return [model, [{ kind: "setSuppressDirty", value: false }]];
        }
        return [model, dirtyCmds(msg.canSchedule, false)];
      }
      if (msg.cleaned !== msg.value) {
        return [
          model,
          [
            { kind: "setSuppressDirty", value: true },
            { kind: "setDoc", value: msg.cleaned },
          ],
        ];
      }
      if (msg.suppressDirty) {
        return [model, [{ kind: "setSuppressDirty", value: false }]];
      }
      return [model, dirtyCmds(msg.canSchedule, msg.hasActiveDatabase)];
    }
    case "RestoreRequested": {
      if (msg.facts.canPersist === false || msg.facts.hasActivePath === false) {
        return stay(model);
      }
      return [model, [{ kind: "restoreVersion", content: msg.content }]];
    }
    case "SaveRequested": {
      if (msg.facts.canPersist === false) return stay(model);
      if (msg.facts.hasActivePath === false) {
        return [model, [{ kind: "requestSaveAs" }]];
      }
      return [model, [{ kind: "markDirtyAndFlush" }]];
    }
    case "SaveFlushed":
      return msg.ok ? [model, [{ kind: "pushCloud" }]] : stay(model);
    case "BlurRequested":
      return [model, [{ kind: "flushForBlur" }]];
    case "BlurFlushed":
      return [model, [{ kind: "blurSyncCloud" }]];
    case "CancelAutosave":
      return [model, [{ kind: "cancelAutosave" }]];
    case "DraftEdited":
      return [model, dirtyCmds(msg.canSchedule, false)];
    default:
      return assertNever(msg);
  }
}

function dirtyCmds(
  canSchedule: boolean,
  refreshLexicon: boolean,
): readonly EditorSaveCmd[] {
  const cmds: EditorSaveCmd[] = [{ kind: "setDirty", value: true }];
  if (canSchedule) cmds.push({ kind: "scheduleAutosave" });
  if (refreshLexicon) cmds.push({ kind: "refreshLexicon" });
  return cmds;
}

export const updateEditorSaveFn: UpdateFn<
  EditorSaveModel,
  EditorSaveMsg,
  EditorSaveCmd
> = updateEditorSave;
